import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';

/**
 * `MutationEntity` is the object emitted by a `MutationObservable`;
 *
 * @export
 * @interface MutationEntity
 */
export interface MutationEntity {
  /**
   * Array of `MutationRecord` objects.
   *
   * @type {MutationRecord[]}
   * @memberof MutationEntity
   */
  mutations: MutationRecord[];

  /**
   * Invoked `MutationObserver` object.
   *
   * @type {MutationObserver}
   * @memberof MutationEntity
   */
  observer: MutationObserver;
}

/**
 * `RxMutationService` is an `RxJs` wrapper around JavaScript
 *  native `MutationObserver` object.
 *
 * @export
 * @class RxMutationService
 */
@Injectable()
export class RxMutationService {
  /**
   * Creates an instance of RxMutationService.
   *
   * @constructor
   * @memberof RxMutationService
   */
  constructor() {}

  /**
   * Creates a mutation observable for the target element and options.
   *
   * @param {Element} target
   * @param {MutationObserverInit} options
   * @returns {Observable<ObserverEntity>}
   * @memberof RxMutationService
   */
  public create(target: Element, options: MutationObserverInit): Observable<MutationEntity> {
    const onNext = (observer: Observer<MutationEntity>) => {
      // Create the mutation observer.
      const mutationObserver = new MutationObserver(
        (mutations: MutationRecord[], invokedObserver: MutationObserver) => {
          // Emit `MutationEntity` object whenever DOM mutation occurs
          observer.next({ mutations, observer: invokedObserver });
        },
      );
      // Observe mutation on a particular node.
      mutationObserver.observe(target, options);
      // Return cleanup function. This callback will be invoked on unsubscribe.
      return () => mutationObserver.disconnect();
    };
    // Create mutation observable.
    const mutation$ = Observable.create(onNext);
    return mutation$;
  }
}
